import { useCallback, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  DefaultButton,
  Panel,
  PanelType,
  TextField,
  Dropdown,
  IDropdownOption,
  IColumn,
  ActionButton,
  IconButton,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi, useMutation } from '../hooks/useApi'
import { useRoles } from '../hooks/useRoles'
import { DataTable } from '../components/DataTable'
import { OwnerPicker } from '../components/OwnerPicker'
import { StatusBadge, CredentialTypeBadge, RiskBadge } from '../components/StatusBadge'
import { AttributeField, AttributeSection } from '../components/AttributeField'
import { APPLICATION_ATTRIBUTES } from '../config/attributeDictionary'
import type { Application, ApplicationFinding, AppOwner, AppPermissionsData, AppHistoryData, AppHistoryEntry, AppHistoryChange, Credential, BusinessUnit, RiskLevel, ApplicationFormData } from '../types'

const SEVERITY_COLOR: Record<string, string> = {
  Critical: '#a4262c',
  High:     '#ca5010',
  Medium:   '#986f0b',
  Low:      '#605e5c',
}

function healthScoreBand(score: number): { label: string; color: string } {
  if (score >= 90) return { label: 'Healthy',  color: '#107c10' }
  if (score >= 70) return { label: 'Fair',     color: '#0078d4' }
  if (score >= 40) return { label: 'At Risk',  color: '#ca5010' }
  return              { label: 'Critical', color: '#a4262c' }
}

function FindingRow({ finding }: { finding: ApplicationFinding }) {
  const color = SEVERITY_COLOR[finding.severity] ?? '#605e5c'
  return (
    <div style={{ borderLeft: `3px solid ${color}`, paddingLeft: 12, marginBottom: 12 }}>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 2 }}>
        <span style={{ color, fontWeight: 600, fontSize: 12 }}>{finding.severity}</span>
        <span style={{ fontWeight: 500, fontSize: 13 }}>{finding.code}</span>
      </Stack>
      {finding.evidence && (
        <Text variant="small" block style={{ color: '#323130', marginBottom: 2 }}>{finding.evidence}</Text>
      )}
      {finding.remediation && (
        <Text variant="small" block style={{ color: '#605e5c' }}>Fix: {finding.remediation}</Text>
      )}
    </div>
  )
}

// Human-readable labels for snapshot diff field names (mirrors ChangeDayView.tsx).
const FIELD_LABELS: Record<string, string> = {
  display_name: 'Display name',
  redirect_uris: 'Redirect URIs',
  preferred_sso_mode: 'SSO mode',
  app_roles: 'App roles',
  key_credentials_count: 'Certificates',
  password_credentials_count: 'Client secrets',
  identifier_uris: 'Identifier URIs',
  logout_url: 'Logout URL',
  homepage: 'Homepage URL',
  reply_urls: 'Reply URLs',
  tags: 'Tags',
  sign_in_audience: 'Sign-in audience',
  account_enabled: 'Account enabled',
  publisher_domain: 'Publisher domain',
  verified_publisher: 'Verified publisher',
}

const EVENT_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  added:    { label: 'Added',    color: '#107c10', bg: '#dff6dd' },
  modified: { label: 'Modified', color: '#ca5010', bg: '#fdf4ec' },
  removed:  { label: 'Removed',  color: '#a4262c', bg: '#fde7e9' },
}

function HistoryChangeRow({ change }: { change: AppHistoryChange }) {
  const label = FIELD_LABELS[change.field] ?? change.field
  const fmt = (v: unknown) => {
    if (v === null || v === undefined) return <em style={{ color: '#a19f9d' }}>none</em>
    if (Array.isArray(v)) return v.length === 0 ? <em style={{ color: '#a19f9d' }}>none</em> : v.join(', ')
    return String(v)
  }

  if (change.added !== undefined || change.removed !== undefined) {
    const added = change.added ?? []
    const removed = change.removed ?? []
    return (
      <div style={{ fontSize: 12, marginBottom: 6 }}>
        <span style={{ fontWeight: 500 }}>{label}: </span>
        {removed.length > 0 && (
          <span style={{ color: '#a4262c', marginRight: 4 }}>−{(removed as unknown[]).join(', ')}</span>
        )}
        {added.length > 0 && (
          <span style={{ color: '#107c10' }}>+{(added as unknown[]).join(', ')}</span>
        )}
      </div>
    )
  }

  return (
    <div style={{ fontSize: 12, marginBottom: 6 }}>
      <span style={{ fontWeight: 500 }}>{label}: </span>
      <span style={{ color: '#605e5c', textDecoration: 'line-through', marginRight: 4 }}>{fmt(change.old_value)}</span>
      <span style={{ color: '#107c10' }}>{fmt(change.new_value)}</span>
    </div>
  )
}

function ConfigHistoryPanel({
  entries,
  loading,
}: {
  entries?: AppHistoryEntry[]
  loading: boolean
}) {
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set())

  const toggle = (date: string) =>
    setExpandedDates(prev => {
      const next = new Set(prev)
      next.has(date) ? next.delete(date) : next.add(date)
      return next
    })

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <h2 className="card-title">Config History</h2>
      {loading ? (
        <Spinner size={SpinnerSize.small} />
      ) : !entries || entries.length === 0 ? (
        <Text style={{ color: '#a19f9d', fontStyle: 'italic', fontSize: 13 }}>
          No config changes recorded in the last 90 days.
        </Text>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          {/* vertical timeline line */}
          <div style={{
            position: 'absolute', left: 7, top: 8, bottom: 8,
            width: 2, background: '#edebe9',
          }} />
          {entries.map(entry => {
            const style = EVENT_STYLE[entry.eventType] ?? EVENT_STYLE.modified
            const isExpanded = expandedDates.has(entry.date)
            const hasChanges = entry.changes.length > 0
            return (
              <div key={entry.date} style={{ marginBottom: 16, position: 'relative' }}>
                {/* dot */}
                <div style={{
                  position: 'absolute', left: -13, top: 4,
                  width: 10, height: 10, borderRadius: '50%',
                  background: style.color, border: '2px solid #fff',
                  boxShadow: '0 0 0 1px #edebe9',
                }} />
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                  <span style={{ fontSize: 12, color: '#605e5c', minWidth: 80 }}>{entry.date}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                    color: style.color, background: style.bg,
                  }}>{style.label}</span>
                  <Link
                    to={`/changes/${entry.date}`}
                    style={{ fontSize: 12, color: '#0078d4', textDecoration: 'none' }}
                  >
                    View day
                  </Link>
                  {hasChanges && (
                    <button
                      onClick={() => toggle(entry.date)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 12, color: '#0078d4', padding: 0,
                      }}
                    >
                      {isExpanded ? 'Hide changes' : `${entry.changes.length} field${entry.changes.length !== 1 ? 's' : ''} changed`}
                    </button>
                  )}
                </Stack>
                {isExpanded && hasChanges && (
                  <div style={{ marginTop: 8, paddingLeft: 4 }}>
                    {entry.changes.map((c, i) => (
                      <HistoryChangeRow key={i} change={c} />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function OwnerRow({
  owner,
  isAdmin,
  onRemove,
  onSetPrimary,
}: {
  owner: AppOwner
  isAdmin: boolean
  onRemove: (ownerId: number) => void
  onSetPrimary: (ownerId: number) => void
}) {
  return (
    <Stack
      horizontal
      verticalAlign="center"
      tokens={{ childrenGap: 8 }}
      style={{ padding: '8px 0', borderBottom: '1px solid #edebe9' }}
    >
      <div style={{ flex: 1 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
          <Text style={{ fontWeight: owner.isPrimary ? 600 : 400 }}>{owner.displayName}</Text>
          {owner.isPrimary && (
            <span style={{
              fontSize: 11, fontWeight: 600, color: '#0078d4',
              background: '#deecf9', borderRadius: 4, padding: '1px 6px',
            }}>Primary</span>
          )}
        </Stack>
        <Text variant="small" style={{ color: '#605e5c' }}>{owner.email}</Text>
      </div>
      {isAdmin && (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          {!owner.isPrimary && (
            <DefaultButton
              text="Set primary"
              styles={{ root: { height: 28, fontSize: 12, minWidth: 0 } }}
              onClick={() => onSetPrimary(owner.ownerId)}
            />
          )}
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Remove owner"
            ariaLabel="Remove owner"
            styles={{ root: { color: '#a4262c' } }}
            onClick={() => onRemove(owner.ownerId)}
          />
        </Stack>
      )}
    </Stack>
  )
}

export function ApplicationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const applicationId = parseInt(id || '0', 10)
  const { isAdmin } = useRoles()

  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [formData, setFormData] = useState<ApplicationFormData>({})

  const [isAddOwnerOpen, setIsAddOwnerOpen] = useState(false)
  const [addOwnerSelectedId, setAddOwnerSelectedId] = useState<number | undefined>()
  const [addOwnerAsPrimary, setAddOwnerAsPrimary] = useState(false)
  const [ownerMutationError, setOwnerMutationError] = useState<string | null>(null)

  const fetchApplication = useCallback(
    () => apiService.getApplication(applicationId),
    [applicationId]
  )
  const fetchCredentials = useCallback(
    () => apiService.getApplicationCredentials(applicationId),
    [applicationId]
  )
  const fetchOwners = useCallback(
    () => apiService.getApplicationOwners(applicationId),
    [applicationId]
  )
  const fetchBusinessUnits = useCallback(() => apiService.getBusinessUnits(), [])
  const fetchRiskLevels = useCallback(() => apiService.getRiskLevels(), [])
  const fetchPermissions = useCallback(
    () => apiService.getApplicationPermissions(applicationId),
    [applicationId]
  )
  const fetchHistory = useCallback(
    () => apiService.getApplicationHistory(applicationId),
    [applicationId]
  )

  const {
    data: application,
    loading: appLoading,
    error: appError,
    refetch: refetchApp,
  } = useApi<Application>(fetchApplication, [applicationId])

  const {
    data: credentials,
    loading: credsLoading,
    error: credsError,
  } = useApi<Credential[]>(fetchCredentials, [applicationId])

  const {
    data: owners,
    loading: ownersLoading,
    refetch: refetchOwners,
  } = useApi<AppOwner[]>(fetchOwners, [applicationId])

  const { data: businessUnits } = useApi<BusinessUnit[]>(fetchBusinessUnits)
  const { data: riskLevels } = useApi<RiskLevel[]>(fetchRiskLevels)
  const {
    data: permissions,
    loading: permissionsLoading,
  } = useApi<AppPermissionsData>(fetchPermissions, [applicationId])

  const {
    data: history,
    loading: historyLoading,
  } = useApi<AppHistoryData>(fetchHistory, [applicationId])

  const updateMutation = useMutation((data: ApplicationFormData) =>
    apiService.updateApplication(applicationId, data)
  )

  const businessUnitOptions: IDropdownOption[] = [
    { key: '', text: 'None' },
    ...(businessUnits?.map(bu => ({ key: bu.businessUnitId, text: bu.name })) || []),
  ]

  const riskLevelOptions: IDropdownOption[] = [
    { key: '', text: 'None' },
    ...(riskLevels?.map(rl => ({ key: rl.riskLevelId, text: rl.name })) || []),
  ]

  const credentialColumns: IColumn[] = [
    {
      key: 'displayName',
      name: 'Name',
      minWidth: 150,
      maxWidth: 250,
      onRender: (item: Credential) => item.displayName || item.hint || 'N/A',
    },
    {
      key: 'type',
      name: 'Type',
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: Credential) => <CredentialTypeBadge type={item.credentialType} />,
    },
    {
      key: 'endDate',
      name: 'Expires',
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: Credential) => new Date(item.endDate).toLocaleDateString(),
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: Credential) => (
        <StatusBadge expiresInDays={item.expiresInDays} isExpired={item.isExpired} />
      ),
    },
    {
      key: 'thumbprint',
      name: 'Identifier',
      minWidth: 150,
      maxWidth: 200,
      onRender: (item: Credential) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {item.thumbprint?.substring(0, 16) || item.hint || '-'}
        </span>
      ),
    },
  ]

  const openEditPanel = () => {
    if (application) {
      setFormData({
        description: application.description || '',
        businessUnitId: application.businessUnitId,
        riskLevelId: application.riskLevelId,
      })
    }
    setIsEditPanelOpen(true)
  }

  const handleSave = async () => {
    const result = await updateMutation.mutate(formData)
    if (result) {
      setIsEditPanelOpen(false)
      refetchApp()
    }
  }

  const handleAddOwner = async () => {
    if (!addOwnerSelectedId) return
    setOwnerMutationError(null)
    try {
      await apiService.addApplicationOwner(applicationId, addOwnerSelectedId, addOwnerAsPrimary)
      setIsAddOwnerOpen(false)
      setAddOwnerSelectedId(undefined)
      setAddOwnerAsPrimary(false)
      refetchOwners()
    } catch (e: unknown) {
      setOwnerMutationError(e instanceof Error ? e.message : 'Failed to add owner')
    }
  }

  const handleRemoveOwner = async (ownerId: number) => {
    setOwnerMutationError(null)
    try {
      await apiService.removeApplicationOwner(applicationId, ownerId)
      refetchOwners()
    } catch (e: unknown) {
      setOwnerMutationError(e instanceof Error ? e.message : 'Failed to remove owner')
    }
  }

  const handleSetPrimary = async (ownerId: number) => {
    setOwnerMutationError(null)
    try {
      await apiService.setPrimaryOwner(applicationId, ownerId)
      refetchOwners()
    } catch (e: unknown) {
      setOwnerMutationError(e instanceof Error ? e.message : 'Failed to set primary owner')
    }
  }

  const healthBand = application?.healthScore != null
    ? healthScoreBand(application.healthScore)
    : null

  // Normalize API data — apiService.request<T> can return {} for empty bodies,
  // which is not an array. Guard all .map/.length call sites here.
  const ownersList: AppOwner[] = Array.isArray(owners) ? owners : []
  const credentialsList: Credential[] = Array.isArray(credentials) ? credentials : []

  if (appLoading) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 400 } }}>
        <Spinner size={SpinnerSize.large} label="Loading application..." />
      </Stack>
    )
  }

  if (appError || !application) {
    return (
      <div className="page-container">
        <MessageBar messageBarType={MessageBarType.error}>
          {appError || 'Application not found'}
        </MessageBar>
        <DefaultButton
          text="Back to Applications"
          onClick={() => navigate('/applications')}
          styles={{ root: { marginTop: 16 } }}
        />
      </div>
    )
  }

  return (
    <div className="page-container">
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="start" style={{ marginBottom: 24 }}>
        <div>
          <DefaultButton
            iconProps={{ iconName: 'Back' }}
            text="Back"
            onClick={() => navigate('/applications')}
            styles={{ root: { marginBottom: 12 } }}
          />
          <h1 className="page-title">{application.displayName}</h1>
          <p className="page-description" style={{ fontFamily: 'monospace' }}>
            {application.entraAppId}
          </p>
        </div>
        {isAdmin && (
          <PrimaryButton
            iconProps={{ iconName: 'Edit' }}
            text="Edit Application"
            onClick={openEditPanel}
          />
        )}
      </Stack>

      {/* ── App Registration section ──────────────────────────────────────── */}
      {/* Integration-shape awareness: only shown when the app has an app
          registration (entraAppId present — all tracked apps have one for now;
          this guard is the hook point for SP-only apps once E2 exposes that). */}
      {application.entraAppId && (
        <AttributeSection title="App Registration" sourceBadge="Entra ID">
          <AttributeField attrKey="entraAppId"      value={application.entraAppId} />
          <AttributeField attrKey="signInAudience"  value={application.signInAudience} />
          <AttributeField attrKey="homepage"        value={application.homepage} />
          <AttributeField attrKey="isVerifiedPublisher" value={application.isVerifiedPublisher} />
        </AttributeSection>
      )}

      {/* ── Enterprise App section ────────────────────────────────────────── */}
      <AttributeSection title="Enterprise App" sourceBadge="Entra ID">
        <AttributeField attrKey="appType"         value={application.appType} />
        <AttributeField attrKey="description"     value={application.description} />
        <AttributeField attrKey="lastSyncedAt"    value={application.lastSyncedAt} />
        <AttributeField attrKey="firstDiscoveredAt" value={application.firstDiscoveredAt} />
      </AttributeSection>

      {/* ── Classification section (SSOps-managed) ────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="card-title" style={{ marginBottom: 16 }}>Classification</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px 24px' }}>
          <AttributeField attrKey="businessUnitName" value={application.businessUnitName} />

          {/* Risk level uses the coloured RiskBadge — custom render outside AttributeField */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Risk Level</Text>
            </div>
            {application.riskLevelName && application.riskLevelColor
              ? <RiskBadge name={application.riskLevelName} color={application.riskLevelColor} />
              : <Text style={{ color: '#a19f9d', fontStyle: 'italic' }}>Not classified</Text>
            }
          </div>

          {/* Health score uses the coloured score pill — custom render */}
          {healthBand && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                  {APPLICATION_ATTRIBUTES.healthScore.label}
                </Text>
              </div>
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 24, borderRadius: 12,
                  background: healthBand.color, color: '#fff', fontSize: 13, fontWeight: 700,
                }}>{application.healthScore}</span>
                <Text style={{ color: healthBand.color }}>{healthBand.label}</Text>
              </Stack>
            </div>
          )}
        </div>
      </div>

      {/* ── Credential Summary ─────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="card-title" style={{ marginBottom: 12 }}>Credential Summary</h3>
        <Stack tokens={{ childrenGap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Total Credentials</Text>
            <Text styles={{ root: { fontWeight: 600 } }}>{application.credentialCount}</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Expired</Text>
            <Text styles={{ root: { fontWeight: 600, color: application.expiredCredentialCount > 0 ? '#a4262c' : undefined } }}>
              {application.expiredCredentialCount}
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Expiring Soon</Text>
            <Text styles={{ root: { fontWeight: 600, color: application.expiringCredentialCount > 0 ? '#ca5010' : undefined } }}>
              {application.expiringCredentialCount}
            </Text>
          </div>
        </Stack>
      </div>

      {/* Owners Card */}
      <div className="card" style={{ marginBottom: 24 }}>
        <Stack horizontal verticalAlign="center" horizontalAlign="space-between" style={{ marginBottom: 12 }}>
          <h2 className="card-title" style={{ margin: 0 }}>
            Owners
            {ownersList.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: '#605e5c' }}>
                {ownersList.length} assigned
              </span>
            )}
          </h2>
          {isAdmin && (
            <ActionButton
              iconProps={{ iconName: 'Add' }}
              text="Add owner"
              onClick={() => { setIsAddOwnerOpen(true); setOwnerMutationError(null) }}
            />
          )}
        </Stack>

        {ownerMutationError && (
          <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 8 } }}>
            {ownerMutationError}
          </MessageBar>
        )}

        {ownersLoading ? (
          <Spinner size={SpinnerSize.small} label="Loading owners..." />
        ) : ownersList.length > 0 ? (
          <div>
            {ownersList.map(o => (
              <OwnerRow
                key={o.appOwnerId}
                owner={o}
                isAdmin={isAdmin}
                onRemove={handleRemoveOwner}
                onSetPrimary={handleSetPrimary}
              />
            ))}
          </div>
        ) : (
          <Text style={{ color: '#a4262c' }}>No owners assigned — assign at least one owner.</Text>
        )}
      </div>

      {/* Credentials Table */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 className="card-title">Credentials</h2>
        {credsError && (
          <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 16 } }}>
            {credsError}
          </MessageBar>
        )}
        <DataTable
          items={credentialsList}
          columns={credentialColumns}
          loading={credsLoading}
          emptyMessage="No credentials found for this application"
          onItemClick={(item) => navigate(`/credentials/${item.credentialId}`)}
        />
      </div>

      {/* ── Permissions Panel ─────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="card-title" style={{ marginBottom: 4 }}>Permissions &amp; Access</h2>
        <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 16 } }}>
          Synced from Entra ID — updated nightly. Empty sections mean the sync has not run yet.
        </Text>

        {permissionsLoading ? (
          <Spinner size={SpinnerSize.small} label="Loading permissions…" />
        ) : (
          <Stack tokens={{ childrenGap: 24 }}>

            {/* Assignments */}
            <div>
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 8 }}>
                <Text styles={{ root: { fontWeight: 600 } }}>Access Assignments</Text>
                <span style={{ fontSize: 12, color: '#605e5c' }}>
                  {permissions?.assignments?.length ?? 0} principal{permissions?.assignments?.length !== 1 ? 's' : ''}
                </span>
              </Stack>
              {permissions?.assignments?.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {permissions.assignments.map(a => {
                    const isAllUsers =
                      a.appRoleId === '00000000-0000-0000-0000-000000000000' || !a.appRoleId
                    return (
                      <div
                        key={a.entraAssignmentId}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '6px 10px', background: '#faf9f8', borderRadius: 4,
                        }}
                      >
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                          background: a.principalType === 'Group' ? '#e8f0fe' : '#f3f2f1',
                          color: a.principalType === 'Group' ? '#1a73e8' : '#323130',
                          minWidth: 44, textAlign: 'center',
                        }}>
                          {a.principalType ?? 'User'}
                        </span>
                        <Text style={{ flex: 1 }}>{a.principalDisplayName ?? a.principalId}</Text>
                        {isAllUsers && (
                          <span style={{
                            fontSize: 11, padding: '1px 6px', borderRadius: 4,
                            background: '#fff4ce', color: '#7a5200',
                          }}>All Users</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <Text style={{ color: '#a19f9d', fontStyle: 'italic', fontSize: 13 }}>
                  No role assignments — access may be open to all users or synced data is pending.
                </Text>
              )}
            </div>

            {/* Permission Grants */}
            <div>
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 8 }}>
                <Text styles={{ root: { fontWeight: 600 } }}>OAuth2 Permission Grants</Text>
                <span style={{ fontSize: 12, color: '#605e5c' }}>
                  {permissions?.permissionGrants?.length ?? 0} grant{permissions?.permissionGrants?.length !== 1 ? 's' : ''}
                </span>
              </Stack>
              {permissions?.permissionGrants?.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {permissions.permissionGrants.map(g => {
                    const scopes = (g.scope ?? '').split(' ').filter(Boolean)
                    const isAdminConsent = g.consentType === 'AllPrincipals'
                    return (
                      <div key={g.entraGrantId} style={{ padding: '8px 10px', background: '#faf9f8', borderRadius: 4 }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 6 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                            background: isAdminConsent ? '#dff6dd' : '#fff4ce',
                            color: isAdminConsent ? '#107c10' : '#7a5200',
                          }}>
                            {isAdminConsent ? 'Admin consent' : 'Per-user consent'}
                          </span>
                        </Stack>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {scopes.map(scope => (
                            <span key={scope} style={{
                              fontFamily: 'monospace', fontSize: 12,
                              padding: '2px 6px', borderRadius: 4,
                              background: '#f3f2f1', color: '#323130',
                            }}>{scope}</span>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <Text style={{ color: '#a19f9d', fontStyle: 'italic', fontSize: 13 }}>
                  No OAuth2 permission grants found.
                </Text>
              )}
            </div>

          </Stack>
        )}
      </div>

      {/* Health Findings */}
      {application.findings && application.findings.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h2 className="card-title">
            Health Findings
            <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: '#605e5c' }}>
              {application.findings.length} open
            </span>
          </h2>
          <div>
            {application.findings.map(f => (
              <FindingRow key={f.findingId} finding={f} />
            ))}
          </div>
        </div>
      )}

      {/* Config History */}
      <ConfigHistoryPanel entries={history?.entries} loading={historyLoading} />

      {/* Add Owner Panel */}
      <Panel
        isOpen={isAddOwnerOpen}
        onDismiss={() => { setIsAddOwnerOpen(false); setAddOwnerSelectedId(undefined) }}
        headerText="Add Owner"
        type={PanelType.smallFixedFar}
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text="Add"
              onClick={handleAddOwner}
              disabled={!addOwnerSelectedId}
            />
            <DefaultButton text="Cancel" onClick={() => setIsAddOwnerOpen(false)} />
          </Stack>
        )}
        isFooterAtBottom
      >
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 20 } }}>
          <OwnerPicker
            label="Owner"
            selectedOwnerId={addOwnerSelectedId}
            onChange={setAddOwnerSelectedId}
            excludeOwnerIds={ownersList.map(o => o.ownerId)}
          />
          <div>
            <input
              type="checkbox"
              id="isPrimary"
              checked={addOwnerAsPrimary}
              onChange={e => setAddOwnerAsPrimary(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="isPrimary" style={{ fontSize: 14 }}>Set as primary owner</label>
          </div>
        </Stack>
      </Panel>

      {/* Edit Panel */}
      <Panel
        isOpen={isEditPanelOpen}
        onDismiss={() => setIsEditPanelOpen(false)}
        headerText="Edit Application"
        type={PanelType.medium}
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text="Save"
              onClick={handleSave}
              disabled={updateMutation.loading}
            />
            <DefaultButton text="Cancel" onClick={() => setIsEditPanelOpen(false)} />
          </Stack>
        )}
        isFooterAtBottom
      >
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 20 } }}>
          {updateMutation.error && (
            <MessageBar messageBarType={MessageBarType.error}>{updateMutation.error}</MessageBar>
          )}
          <div>
            <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 4 } }}>
              Display Name (synced from Entra ID, read-only)
            </Text>
            <Text block styles={{ root: { fontWeight: 500 } }}>{application?.displayName}</Text>
          </div>
          <TextField
            label="Description"
            value={formData.description}
            onChange={(_, val) => setFormData({ ...formData, description: val })}
            multiline
            rows={3}
          />
          <Dropdown
            label="Business Unit"
            options={businessUnitOptions}
            selectedKey={formData.businessUnitId || ''}
            onChange={(_, opt) => setFormData({ ...formData, businessUnitId: opt?.key ? Number(opt.key) : undefined })}
          />
          <Dropdown
            label="Risk Level"
            options={riskLevelOptions}
            selectedKey={formData.riskLevelId || ''}
            onChange={(_, opt) => setFormData({ ...formData, riskLevelId: opt?.key ? Number(opt.key) : undefined })}
          />
        </Stack>
      </Panel>
    </div>
  )
}
